<template>
  <div class="audit-wrapper">
    <a-card class="audit-card" :loading="loading" bordered>
      <template #title>
        <div class="title">
          <span>审计日志</span>
          <span class="sub">记录网关身份、请求路径、方法、状态与耗时</span>
        </div>
      </template>
      <template #extra>
        <a-space :size="10">
          <a-input v-model:value="pathFilter" placeholder="路径" allow-clear style="width: 180px" @pressEnter="handleSearch" />
          <a-select v-model:value="methodFilter" allow-clear placeholder="方法" style="width: 120px" @change="handleSearch">
            <a-select-option value="GET">GET</a-select-option>
            <a-select-option value="POST">POST</a-select-option>
            <a-select-option value="PUT">PUT</a-select-option>
            <a-select-option value="PATCH">PATCH</a-select-option>
            <a-select-option value="DELETE">DELETE</a-select-option>
          </a-select>
          <a-input v-model:value="statusFilter" placeholder="状态码" allow-clear style="width: 120px" @pressEnter="handleSearch" />
          <a-segmented v-model:value="range" :options="rangeOptions" @change="handleSearch" />
        </a-space>
      </template>
      <a-table
        :data-source="dataSource"
        :columns="columns"
        :pagination="pagination"
        :row-key="record => record.id"
        size="middle"
        @change="handleTableChange"
      >
        <template #createdAt="{ text }">
          {{ formatDate(text) }}
        </template>
        <template #user="{ record }">
          <span>{{ record.authUsername || record.authUserId || '-' }}</span>
        </template>
        <template #path="{ record }">
          <div class="path-cell" :title="record.path">
            {{ record.method }} {{ record.path || '-' }}
          </div>
        </template>
        <template #status="{ record }">
          <a-tag :color="record.statusCode >= 400 ? 'red' : 'blue'">{{ record.statusCode }}</a-tag>
        </template>
      </a-table>
    </a-card>
  </div>
</template>

<script>
import { defineComponent, onMounted, reactive, ref } from 'vue'
import { getAuditLogs } from '@/api/TikuComponentApi'
import { message } from 'ant-design-vue'
import { asPageResult } from '@/utils/result'

export default defineComponent({
  name: 'AuditLog',
  setup() {
    const dataSource = ref([])
    const loading = ref(false)
    const pathFilter = ref('')
    const methodFilter = ref(undefined)
    const statusFilter = ref('')
    const range = ref('24h')
    const rangeOptions = [
      { label: '24H', value: '24h' },
      { label: '7天', value: '7d' },
      { label: '14天', value: '14d' },
      { label: '全部', value: 'all' },
    ]
    const pagination = reactive({
      current: 1,
      pageSize: 20,
      total: 0,
      showSizeChanger: true,
      showQuickJumper: true,
      pageSizeOptions: ['20', '50', '100']
    })
    const columns = [
      { title: '时间', dataIndex: 'createdAt', key: 'createdAt', width: 180, slots: { customRender: 'createdAt' } },
      { title: '用户', dataIndex: 'authUsername', key: 'authUsername', width: 160, slots: { customRender: 'user' } },
      { title: '请求', dataIndex: 'path', key: 'path', ellipsis: true, slots: { customRender: 'path' } },
      { title: '状态', dataIndex: 'statusCode', key: 'statusCode', width: 100, slots: { customRender: 'status' } },
      { title: '耗时(ms)', dataIndex: 'latencyMs', key: 'latencyMs', width: 120 },
      { title: 'IP', dataIndex: 'clientIp', key: 'clientIp', width: 140 },
      { title: 'Request ID', dataIndex: 'requestId', key: 'requestId', width: 220, ellipsis: true },
    ]

    const buildStart = (val) => {
      const now = Date.now()
      switch (val) {
        case '24h':
          return new Date(now - 24 * 60 * 60 * 1000)
        case '7d':
          return new Date(now - 7 * 24 * 60 * 60 * 1000)
        case '14d':
          return new Date(now - 14 * 24 * 60 * 60 * 1000)
        default:
          return null
      }
    }

    const fetchData = () => {
      loading.value = true
      const params = {
        page: pagination.current,
        pageSize: pagination.pageSize,
      }
      const start = buildStart(range.value)
      if (start) params.start = start.toISOString()
      if (pathFilter.value) params.path = pathFilter.value
      if (methodFilter.value) params.method = methodFilter.value
      if (statusFilter.value) params.status = statusFilter.value
      getAuditLogs(params).then((res) => {
        if (res?.status === 200) {
          const pageResult = asPageResult(res.data, pagination.pageSize)
          dataSource.value = pageResult.data
          pagination.total = pageResult.total
          pagination.current = pageResult.page
          pagination.pageSize = pageResult.pageSize
        } else {
          dataSource.value = []
          message.error(res?.message || '获取审计日志失败')
        }
      }).finally(() => {
        loading.value = false
      })
    }

    const handleTableChange = (pager) => {
      pagination.current = pager.current
      pagination.pageSize = pager.pageSize
      fetchData()
    }

    const handleSearch = () => {
      pagination.current = 1
      fetchData()
    }

    const formatDate = (dateString) => {
      if (!dateString) return '-'
      const date = new Date(dateString)
      if (isNaN(date.getTime())) return '-'
      const Y = date.getFullYear()
      const M = String(date.getMonth() + 1).padStart(2, '0')
      const D = String(date.getDate()).padStart(2, '0')
      const h = String(date.getHours()).padStart(2, '0')
      const m = String(date.getMinutes()).padStart(2, '0')
      const s = String(date.getSeconds()).padStart(2, '0')
      return `${Y}-${M}-${D} ${h}:${m}:${s}`
    }

    onMounted(fetchData)

    return {
      dataSource,
      loading,
      columns,
      pagination,
      pathFilter,
      methodFilter,
      statusFilter,
      range,
      rangeOptions,
      formatDate,
      handleTableChange,
      handleSearch,
    }
  }
})
</script>

<style scoped>
.audit-wrapper {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  background-color: #f0f2f5;
  min-height: calc(100vh - 120px);
}

.title {
  display: flex;
  flex-direction: column;
}

.title span:first-child {
  font-size: 18px;
  font-weight: 600;
  color: #1f1f1f;
}

.title .sub {
  color: #8c8c8c;
  font-size: 13px;
  margin-top: 4px;
  font-weight: normal;
}

.audit-card {
  background: #fff;
  border-radius: 16px;
  border: 1px solid #f0f0f0;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.03);
}

.path-cell {
  max-width: 420px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #333;
}

:deep(.ant-table-thead > tr > th) {
  background: #fafafa;
  font-weight: 600;
}

:deep(.ant-table-tbody > tr:hover > td) {
  background: #f0f7ff !important;
}
</style>
